import type { SaleVerdict } from '@krp/client-runtime'
import {
  formatKopecks,
  type ParkVm,
  type ProgressStepState,
  type ProgressStepVm,
  type ProgressToolFieldVm,
  type ProgressToolVm,
  type ProgressVm,
  type ReceiptRetryVm,
} from '@krp/sale-ui'

import { toolErrors, type OwnToolKey, type OwnWordsState } from './words'

/**
 * Ход проведения собственного ядра, когда деньги взяты (или могли быть взяты), а чек не прошёл: ровно то, что решил владелец
 * (18.10.2026) — две отметки всегда, по центру ОДНА основная кнопка («Повторить проведение»), остальное под «Подробнее». Автоотмены
 * платежа нет: отменить платёж может только человек на терминале и сказать об этом окну («Оплата отменена вручную»).
 * Тексты агента показываются дословно; RRN — только последние четыре знака и сумма. Чистая функция, ничего не читает и не шлёт.
 */

export type UnknownVerdict = Extract<SaleVerdict, { kind: 'unknown' }>

export interface OwnProgressInput {
  verdict: UnknownVerdict
  /** Сумма чека в копейках: у продажи окна — на момент оплаты, у записи из журнала — из строки журнала; нет — не показываем. */
  amountKopecks: number | null
  /** Ключ операции у агента; `null` — не известен (связь оборвалась до ответа), тогда действий над записью нет. */
  operationId: string | null
  words: OwnWordsState
  /** Идёт другая команда агенту. */
  busy: boolean
}

/** Отказы, после которых вместо «Повторить проведение» предлагается «Сверить с кассой» (сначала проверить, нет ли уже документа). */
const RECONCILE_CODES: ReadonlySet<string> = new Set(['document-may-exist', 'fn-changed', 'fn-replaced'])

/** Отказ агента, после которого вместо повтора предлагается сверка с кассой. */
export const isReconcileCode = (code: string | null): boolean => code !== null && RECONCILE_CODES.has(code)

export const DOUBLE_RECEIPT_TEXT = 'Возможно, пробито два чека. Проверьте ленту и накопитель кассы, прежде чем что-то делать дальше.'

/** «Повторить проведение» уместно ли предложить; `true` — деньги за чек взяты (наличные получены или карта одобрена). */
export function moneyTaken(verdict: UnknownVerdict): boolean {
  const card = verdict.card
  if (card === null) return verdict.error?.code !== 'agent-unreachable'
  return card.outcome === 'approved'
}

const step = (key: ProgressStepVm['key'], label: string, state: ProgressStepState, text: string): ProgressStepVm => ({ key, label, state, text })

function stepsOf(cardPaid: boolean, payment: ProgressStepState, paymentText: string, receipt: ProgressStepState, receiptText: string): readonly ProgressStepVm[] {
  return [
    step('payment', cardPaid ? '① Оплата (терминал)' : 'Наличные получены', payment, paymentText),
    step('receipt', '② Чек (фискальный накопитель)', receipt, receiptText),
  ]
}

/** Последние четыре знака RRN: по ним находят платёж на терминале, а полный номер на экране не нужен. */
export function rrnTail(rrn: string | null): string | null {
  if (rrn === null) return null
  const digits = rrn.trim()
  return digits.length === 0 ? null : digits.slice(-4)
}

function fieldsOf(key: OwnToolKey, values: Readonly<Record<string, string>>): readonly ProgressToolFieldVm[] {
  const errors = toolErrors(key, values)
  const shown = (field: string): string | null => ((values[field] ?? '') === '' ? null : (errors[field] ?? null))
  const value = (field: string): string => values[field] ?? ''
  switch (key) {
    case 'correction':
      return [
        { key: 'lastFiscalDocument', label: 'Номер последнего документа на кассе', kind: 'number', value: value('lastFiscalDocument'), required: true, hint: 'Посмотрите его на самой кассе (копия последнего чека или X-отчёт): так агент убеждается, что вы проверили кассу.', error: shown('lastFiscalDocument') },
        {
          key: 'correctionType',
          label: 'Вид коррекции',
          kind: 'select',
          value: value('correctionType'),
          options: [
            { value: 'self', label: 'По самостоятельному решению' },
            { value: 'instruction', label: 'По предписанию' },
          ],
          required: true,
          error: null,
        },
        { key: 'baseDate', label: 'Дата основания (ГГГГ.ММ.ДД)', kind: 'text', value: value('baseDate'), required: false, hint: 'Пусто — дата расчёта.', error: shown('baseDate') },
        { key: 'baseNumber', label: values.correctionType === 'instruction' ? 'Номер предписания' : 'Номер основания', kind: 'text', value: value('baseNumber'), required: values.correctionType === 'instruction', error: values.correctionType === 'instruction' && errors.baseNumber !== undefined && value('baseNumber') === '' ? null : shown('baseNumber') },
      ]
    case 'repunch-number':
      return [
        { key: 'lastFiscalDocument', label: 'Номер последнего документа на кассе', kind: 'number', value: value('lastFiscalDocument'), required: true, hint: 'Посмотрите его на самой кассе (копия последнего чека или X-отчёт).', error: shown('lastFiscalDocument') },
      ]
    case 'close':
      return [
        {
          key: 'charged',
          label: 'Деньги списаны с карты?',
          kind: 'select',
          value: value('charged'),
          options: [
            { value: '', label: 'Выберите…' },
            { value: 'no', label: 'Нет, деньги не списаны' },
            { value: 'yes', label: 'Да, списаны' },
          ],
          required: true,
          error: null,
        },
        {
          key: 'checked',
          label: 'Что вы проверили',
          kind: 'select',
          value: value('checked'),
          options: [
            { value: '', label: 'Выберите…' },
            ...CARD_CHECKED_OPTIONS.map((option) => ({ value: option, label: option })),
          ],
          required: true,
          hint: 'Агент записывает, на чём основано ваше слово, и не принимает его без этого — ни при «списаны», ни при «не списаны».',
          error: shown('checked'),
        },
        {
          key: 'reason',
          label: 'Причина закрытия',
          kind: 'text',
          value: value('reason'),
          required: true,
          hint: 'Своими словами, что именно вы увидели: например «слип с отказом банка» или «платёж есть в приложении банка». Запись остаётся в журнале агента.',
          error: shown('reason'),
        },
        ...(value('charged') === 'yes'
          ? [
              { key: 'rrn', label: 'RRN платежа (как на слипе терминала)', kind: 'text' as const, value: value('rrn'), required: true, error: shown('rrn') },
              { key: 'authCode', label: 'Код авторизации (если есть)', kind: 'text' as const, value: value('authCode'), required: false, error: null },
            ]
          : []),
      ]
    case 'confirm-void':
      return []
  }
}

/** Что человек мог проверить на терминале: слова уходят агенту как `checked` и пишутся в его журнал. */
const CARD_CHECKED_OPTIONS: readonly string[] = ['Слип терминала', 'Экран терминала', 'Приложение или выписка банка']

interface ToolSpec {
  label: string
  submitLabel: string
  text: string
  danger: boolean
}

const TOOLS: Readonly<Record<OwnToolKey, ToolSpec>> = {
  correction: {
    label: 'Чек коррекции',
    submitLabel: 'Пробить чек коррекции',
    text: 'Чек коррекции — настоящий фискальный документ, отменить его нельзя. Нужен, только если обычный чек пробить не удаётся. Деньги по карте он не трогает.',
    danger: true,
  },
  'repunch-number': {
    label: 'Повторить, указав номер документа',
    submitLabel: 'Повторить проведение',
    text: 'Запасной путь, когда агент не может сам исключить, что документ уже есть на кассе. Повтор пробьёт чек заново на этой же кассе.',
    danger: false,
  },
  close: {
    label: 'Закрыть с причиной',
    submitLabel: 'Закрыть',
    text: 'Посмотрите терминал и слип. Если деньги не списаны, продажа закроется как не проведённая. Если списаны — укажите RRN: агент сверит накопитель кассы.',
    danger: true,
  },
  'confirm-void': {
    label: 'Оплата отменена вручную',
    submitLabel: 'Оплата отменена, закрыть',
    text: 'Нажимайте только после того, как сами отменили платёж на терминале или убедились, что деньги вернулись: окно платёж не отменяет. Продажа закроется как не проведённая.',
    danger: true,
  },
}

function toolOf(key: OwnToolKey, words: OwnWordsState, busy: boolean): ProgressToolVm {
  const spec = TOOLS[key]
  const open = words.tool?.key === key ? words.tool : null
  const sending = words.sending === key
  if (open === null) {
    return { key, label: spec.label, canOpen: !busy && words.sending === null, blockedText: null, form: null, busy: false }
  }
  const errors = toolErrors(key, open.values)
  const first = Object.values(errors)[0] ?? null
  return {
    key,
    label: spec.label,
    canOpen: true,
    blockedText: null,
    busy: sending,
    form: {
      text: spec.text,
      fields: fieldsOf(key, open.values),
      submitLabel: spec.submitLabel,
      canSubmit: first === null && !busy && !sending,
      submitBlockedReason: first,
      sending,
      hint: words.refusal?.text ?? null,
      danger: spec.danger,
    },
  }
}

const PARK_TEXT =
  'Касса освободится для следующей продажи. Деньги не возвращаются и запись не теряется: она останется среди отложенных, решить можно позже. ' +
  'Если исход чека неизвестен, перед повтором проверьте ленту — иначе можно пробить второй чек.'

function parkOf(words: OwnWordsState, busy: boolean): ParkVm {
  const sending = words.sending === 'park'
  return {
    label: 'Отложить продажу',
    canOpen: !busy && words.sending === null,
    blockedText: null,
    form: words.parkOpen
      ? {
          confirmText: PARK_TEXT,
          reason: '',
          reasonVisible: false,
          canSubmit: !busy && !sending,
          submitBlockedReason: null,
          sending,
          hint: words.refusal?.text ?? null,
        }
      : null,
  }
}

export function buildUnresolvedProgress(input: OwnProgressInput): ProgressVm {
  const { verdict, words, busy } = input
  const card = verdict.card
  const cardPaid = card !== null
  const error = verdict.error
  const meta = verdict.meta
  const unreachable = error?.code === 'agent-unreachable'
  const cardUnknown = card?.outcome === 'unknown'
  const noDocument = error?.code === 'no-document-found'
  const taken = moneyTaken(verdict)
  const amountText = input.amountKopecks === null ? '' : formatKopecks(input.amountKopecks)
  const operationId = input.operationId
  const own = words.refusal !== null && words.refusal.operationId === operationId ? words.refusal : null

  // Подробности: совет агента, номер попытки, RRN-4 и сумма. Слова агента о причине - в тексте окна, а не здесь.
  const details: string[] = []
  if (error?.action != null) details.push(error.action)
  if (meta?.attempt != null && meta.attempt > 1) details.push(`Попытка чека: ${meta.attempt}`)
  const tail = rrnTail(card?.rrn ?? null)
  if (tail !== null) details.push(`RRN …${tail}`)
  if (card !== null && card.outcome !== 'unknown' && input.amountKopecks !== null) details.push(`Сумма ${amountText}`)

  const warnings: string[] = []
  if (meta?.possibleDoubleReceipt === true) warnings.push(DOUBLE_RECEIPT_TEXT)
  if (meta?.notice != null) warnings.push(meta.notice)

  const base = { amountText, canCancelPayment: false, canFiscalize: false, canMarkOutside: false, details, warnings, waiting: false }

  // Основная кнопка: только когда деньги взяты, а чека нет; «Сверить с кассой» — когда агент не может исключить документ на кассе.
  const reconcile = own !== null && own.code !== null && RECONCILE_CODES.has(own.code)
  const retry: ReceiptRetryVm | null =
    operationId === null || !taken
      ? null
      : {
          label: reconcile ? 'Сверить с кассой' : 'Повторить проведение',
          canOpen: !busy && words.sending === null && meta?.canRepunch !== false,
          blockedText: own?.text ?? (meta?.canRepunch === false ? 'Агент сейчас не разрешает повторить проведение.' : null),
          form: null,
        }

  const tools: ProgressToolVm[] = []
  if (operationId !== null) {
    if (taken) {
      tools.push(toolOf('correction', words, busy), toolOf('repunch-number', words, busy))
    }
    if (cardUnknown) tools.push(toolOf('close', words, busy))
    if (card?.outcome === 'approved') tools.push(toolOf('confirm-void', words, busy))
  }
  const common = { ...base, retry, park: operationId === null ? null : parkOf(words, busy), tools }

  if (unreachable) {
    return {
      ...common,
      steps: stepsOf(cardPaid, cardPaid ? 'unknown' : 'done', cardPaid ? 'неизвестно' : 'получены', 'unknown', 'неизвестно'),
      phase: 'command-unknown',
      title: 'Нет ответа от кассового агента',
      text: 'Чек мог быть пробит. Не повторяйте его: проверьте кассу, окно само выяснит исход, когда агент вернётся.',
      tone: 'warning',
      manualRequired: false,
    }
  }
  if (cardUnknown) {
    return {
      ...common,
      steps: stepsOf(true, 'unknown', 'неизвестно', 'waiting', 'после оплаты'),
      phase: 'money-unknown',
      title: 'Оплата картой: исход неизвестен',
      text: [error?.text ?? 'Терминал не подтвердил оплату.', error?.action ?? '', 'Посмотрите терминал и слип: списаны ли деньги. Окно платёж не отменяет и сам ничего не решает.'].filter((part) => part !== '').join(' '),
      tone: 'danger',
      // Окно не умеет «Ручную сверку» этого вида: выход один — «Закрыть с причиной» под «Подробнее» (раскрыто само, основной кнопки нет).
      manualRequired: false,
    }
  }
  if (noDocument) {
    return {
      ...common,
      steps: stepsOf(cardPaid, 'done', cardPaid ? 'оплачено' : 'получены', 'failed', 'не пробит'),
      phase: 'no-receipt',
      title: 'Деньги взяты, чека нет',
      text: error?.text ?? 'По накопителю документа нет.',
      tone: 'danger',
      // Кнопки «Ручная сверка» у «Собственной» нет: выход — «Повторить проведение» и действия под «Подробнее» (коррекция, номер последнего документа).
      manualRequired: false,
    }
  }
  return {
    ...common,
    steps: stepsOf(cardPaid, 'done', cardPaid ? 'оплачено' : 'получены', meta?.oneCStage === 'FiscalRefused' ? 'failed' : 'unknown', meta?.oneCStage === 'FiscalRefused' ? 'ошибка чека' : 'неизвестно'),
    phase: 'receipt-unknown',
    title: meta?.oneCStage === 'FiscalRefused' ? 'Ошибка чека' : 'Исход чека неизвестен',
    text: [error?.text ?? 'Чек мог быть пробит.', error?.action, '«Повторить проведение» сначала сверит накопитель: если чек уже есть, второй не пробьётся.'].filter(Boolean).join(' '),
    tone: 'warning',
    manualRequired: false,
  }
}
